/**
 * /models — the open-weight models OculusMind.AI publishes (owner request
 * 2026-10-07: highlight Pico V2 with its Hugging Face link, the production-eval
 * population table, and the BFCL V4 per-category table).
 *
 * Public. Every figure comes from `config/models.ts`, which mirrors the published
 * model card; this page only lays it out and derives the deltas. The framing
 * paragraph under the population table is not decoration: those evals measure
 * agreement with OculusMind.AI's own production behaviour, which the model was
 * trained to reproduce, and say nothing about general capability. A page that
 * lifted the numbers without that would claim more than they show.
 *
 * Pico V2 against its base only (owner, 2026-10-08: remove all Haiku references
 * from this page; the model card moves its Claude comparisons to an appendix).
 */
import { AGENTS } from '../agents/registry.ts';
import {
  BFCL_ROWS,
  PICO_V1,
  PICO_V2,
  PICO_V2_OLLAMA,
  POPULATION_ROWS,
  bfclOverall,
  type PopulationRow,
} from '../config/models.ts';
import './Models.css';

type Direction = 'better' | 'worse' | 'same';

interface Delta {
  readonly text: string;
  readonly direction: Direction;
}

/** Change from `from` to `to` in percentage points, with the relative change.
 *  Computed from the two printed values, as a reader checking the row would. */
function delta(from: number, to: number, lowerIsBetter = false): Delta {
  const points = Number((to - from).toFixed(2));
  if (points === 0) {
    return { text: '±0', direction: 'same' };
  }
  const better = lowerIsBetter ? points < 0 : points > 0;
  const sign = points > 0 ? '+' : '−';
  const size = `${sign}${Math.abs(points).toFixed(2)} pts`;
  if (lowerIsBetter && to === 0) {
    return { text: `${size} — eliminated`, direction: 'better' };
  }
  const relative = ((to - from) / from) * 100;
  return {
    text: `${size} (${sign}${Math.abs(relative).toFixed(1)} %)`,
    direction: better ? 'better' : 'worse',
  };
}

function DeltaCell({ value }: { readonly value: Delta }) {
  return (
    <td className={`om-models__num om-models__delta om-models__delta--${value.direction}`}>
      {value.text}
      {value.direction === 'same' ? null : (
        <span className="om-models__sr"> ({value.direction})</span>
      )}
    </td>
  );
}

function Score({ row, value }: { readonly row: PopulationRow; readonly value: number }) {
  if (row.metric === 'loops') {
    // Any non-zero loop rate is the defect this model was selected against.
    return (
      <span className={value > 0 ? 'om-models__flag' : undefined}>{value.toFixed(2)} %</span>
    );
  }
  return <>{value.toFixed(2)}</>;
}

function PopulationTable() {
  return (
    <div className="om-models__scroll">
      <table className="om-models__table om-models__table--populations">
        <caption className="om-models__sr">
          Agreement with OculusMind.AI production tool choice, by eval population
        </caption>
        <thead>
          <tr>
            <th scope="col">Eval population</th>
            <th scope="col" className="om-models__num">Base (untrained)</th>
            <th scope="col" className="om-models__num">Pico V2</th>
            <th scope="col" className="om-models__num">Δ vs base</th>
          </tr>
        </thead>
        <tbody>
          {POPULATION_ROWS.map((row) => {
            const lower = row.metric === 'loops';
            return (
              <tr key={`${row.population} ${row.detail}`}>
                <th scope="row">
                  <span className="om-models__pop">{row.population}</span>
                  <span className="om-models__detail">{row.detail}</span>
                </th>
                <td className="om-models__num"><Score row={row} value={row.base} /></td>
                <td className="om-models__num om-models__ours"><Score row={row} value={row.pico} /></td>
                <DeltaCell value={delta(row.base, row.pico, lower)} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BfclTable() {
  return (
    <div className="om-models__scroll">
      <table className="om-models__table">
        <caption className="om-models__sr">BFCL V4 accuracy by category, Pico V2 against its base</caption>
        <thead>
          <tr>
            <th scope="col">Category (items)</th>
            <th scope="col" className="om-models__num">Base</th>
            <th scope="col" className="om-models__num">Pico V2</th>
            <th scope="col" className="om-models__num">Δ vs base</th>
          </tr>
        </thead>
        <tbody>
          {BFCL_ROWS.map((row) => (
            <tr key={row.category}>
              <th scope="row">
                <code>{row.category}</code> <span className="om-models__n">({row.n.toLocaleString('en-US')})</span>
              </th>
              <td className="om-models__num">{row.base.toFixed(2)}</td>
              <td className="om-models__num om-models__ours">{row.pico.toFixed(2)}</td>
              <DeltaCell value={delta(row.base, row.pico)} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Models() {
  const uno = AGENTS.uno;
  const overallPico = bfclOverall(BFCL_ROWS, 'pico').toFixed(2);
  const overallBase = bfclOverall(BFCL_ROWS, 'base').toFixed(2);
  const ahead = BFCL_ROWS.filter((r) => r.pico > r.base).length;
  return (
    <div className="om-models">
      <header className="om-models__head">
        <span className="om-models__eyebrow">Models</span>
        <h1 className="om-models__h1">Open models for agent work</h1>
        <p className="om-models__lede">
          We fine-tune open-weight language models on the tool calls our agents make in production,
          measure them against the work they will do, and publish them under Apache&#8209;2.0.
        </p>
      </header>

      <section className="om-model" aria-labelledby="om-model-pico-v2">
        <div className="om-model__intro">
          <figure className="om-model__portrait">
            <img src={uno.image} alt="" width={160} height={160} decoding="async" />
            <figcaption>{uno.name} — the agent whose tool-calling traffic trained Pico V2</figcaption>
          </figure>
          <div className="om-model__copy">
            <span className="om-model__kicker">Latest release · October 2026</span>
            <h2 id="om-model-pico-v2" className="om-model__name">{PICO_V2.name}</h2>
            <p>
              An 8-billion-parameter tool-calling model that runs on a laptop. Pico V2 is Mistral&apos;s
              Ministral-3-8B-Instruct-2512, fine-tuned on the production tool calls of {uno.name}, our
              assistant agent, and chosen from a ten-seed sweep under one hard rule: a seed that ever
              looped was out, whatever it scored.
            </p>
            <ul className="om-model__facts" aria-label="Pico V2 at a glance">
              <li>8B parameters</li>
              <li>Apache-2.0</li>
              <li>Q8_0 GGUF · BF16 safetensors · LoRA adapter</li>
              <li>No tool-call loops on any OculusMind.AI eval population</li>
            </ul>
            <a className="om-model__cta" href={PICO_V2.url}>
              Model card and downloads on Hugging Face →
            </a>
            <p className="om-model__ollama">
              Or run it locally in one line with{' '}
              <a href={PICO_V2_OLLAMA.url}>Ollama</a>:{' '}
              <code className="om-model__command">{PICO_V2_OLLAMA.command}</code>
            </p>
          </div>
        </div>

        <h3 className="om-models__h3">OculusMind.AI production evals</h3>
        <p className="om-models__text">
          Held-out production traffic, scored per tool-turn: did the model choose the same first tool
          our production agent chose, with valid arguments, and without a degenerate repeat? Same scorer
          and generation cap for every model; Pico V2 and its base run as the published Q8_0 GGUF.
        </p>
        <PopulationTable />
        <p className="om-models__note">
          <strong>What these numbers measure.</strong> Agreement with OculusMind.AI&apos;s own production
          tool choice on turns held out from training — the behaviour Pico V2 was fine-tuned to
          reproduce. They show how closely a local 8B model reproduces our production agents on this
          workload. They are not a general capability ranking.
        </p>

        <h3 className="om-models__h3">BFCL V4, by category</h3>
        <p className="om-models__text">
          The Berkeley Function Calling Leaderboard V4: {BFCL_ROWS.length} categories,{' '}
          {BFCL_ROWS.reduce((sum, r) => sum + r.n, 0).toLocaleString('en-US')} items, Pico V2 against
          its untrained base on the same harness revision (the agentic web-search and memory categories
          were not run). Pico V2 is ahead on {ahead} of {BFCL_ROWS.length}, and {overallPico} % against{' '}
          {overallBase} % overall, item-weighted.
        </p>
        <BfclTable />
        <p className="om-models__note">
          It trails only on the two <em>irrelevance</em> categories — the ones that reward declining to
          call a tool — by 2–3 points: it was trained on traffic where reaching for a tool is the norm.
        </p>
      </section>

      <section className="om-models__earlier" aria-labelledby="om-models-earlier">
        <h2 id="om-models-earlier" className="om-models__h3">Earlier releases</h2>
        <a className="om-models__release" href={PICO_V1.url}>
          <span className="om-models__release-name">{PICO_V1.name}</span>
          <span className="om-models__detail">
            Pico V1 — the first generation of the series, on the same base model. Apache-2.0.
          </span>
        </a>
      </section>
    </div>
  );
}
